import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { Button } from 'ncoded-component-library';
import AddTeamMemberModal from '../AddTeamMemberModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import ManageTeamMemberDrawer from '../ManageTeamMemberDrawer';
import DataRoomMembersTable from 'router/subrouters/DataRoom/components/DataRoomMembersTable';
import PlusIcon from 'icons/Plus.icon';

import './TeamMembersTable.styles.scss';
import './TeamMembersTable.styles.responsive.scss';

type TeamMembersTableProps = {
  className?: string;
};

const TeamMembersTable: React.FC<TeamMembersTableProps> = (props) => {
  const { className } = props;

  const [selectedMember, setSelectedMember] = useState<DataRoomMember>();

  const { buySideGroups, setBuySideGroups } = useContext(MembersContext);

  const { dataRoomMembers: buySideMembers, _id: buySideGroupId } =
    buySideGroups?.[0] || {};

  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const [manageMember, manageMemberRef] = useCallbackRef<SideDrawerRef>(null);

  const classes = classNames('team-members-table', className);

  const { t } = useTranslation();

  const handleOnEdit = useCallback(
    (member: DataRoomMember) => {
      setSelectedMember(member);
      manageMember.setOpen(true);
    },
    [manageMember],
  );

  const updateMember = useCallback(
    (member: DataRoomMember) => {
      setSelectedMember((prev) => ({ ...prev, ...member }));

      setBuySideGroups((prev) =>
        prev.map((group) => {
          if (group.dataRoomMembers?.every(({ _id }) => _id !== member._id)) {
            return group;
          }

          return {
            ...group,
            dataRoomMembers: group.dataRoomMembers.map((m) =>
              m._id !== member._id ? m : member,
            ),
          };
        }),
      );
    },
    [setBuySideGroups],
  );

  const updateDataRoomMembers = useCallback(
    ({ buySideGroups }: DataRoomMembers) => {
      setBuySideGroups(buySideGroups);
    },
    [setBuySideGroups],
  );

  if (!buySideMembers?.length) return null;

  return (
    <div className="white-panel team-members-table-wrapper">
      <div className="team-members-table-wrapper__actions">
        <Button variant="outline" icon={PlusIcon} onClick={modal?.open}>
          {t('addTeamMemberLC')}
        </Button>
      </div>

      <DataRoomMembersTable
        isBuySide
        className={classes}
        members={buySideMembers}
        onEdit={handleOnEdit}
      />

      <AddTeamMemberModal
        ref={modalRef}
        buySideGroupId={buySideGroupId}
        updateDataRoomMembers={updateDataRoomMembers}
      />

      <ManageTeamMemberDrawer
        ref={manageMemberRef}
        member={selectedMember}
        updateMember={updateMember}
        updateDataRoomMembers={updateDataRoomMembers}
      />
    </div>
  );
};

export default TeamMembersTable;
